import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Flame, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function ReadingGoalSettings() {
  const { user } = useAuth();
  const [weeklyGoal, setWeeklyGoal] = useState<number>(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadGoal();
  }, [user]);

  const loadGoal = async () => {
    try {
      const { data } = await supabase
        .from('user_reading_preferences')
        .select('weekly_reading_goal')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (data) {
        setWeeklyGoal(data.weekly_reading_goal);
      }
    } catch (error) {
      console.error('Error loading reading goal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_reading_preferences')
        .upsert(
          {
            user_id: user.id,
            weekly_reading_goal: weeklyGoal,
          },
          { onConflict: 'user_id' }
        );

      if (error) throw error;

      // Also update the current week's goal in user_streaks
      await supabase
        .from('user_streaks')
        .update({ weekly_goal_days: weeklyGoal })
        .eq('user_id', user.id);

      toast.success('Reading goal updated! 🎯');
    } catch (error) {
      console.error('Error saving reading goal:', error);
      toast.error('Failed to update reading goal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Weekly Reading Goal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          Weekly Reading Goal
        </CardTitle>
        <CardDescription>
          Set how many days per week you want to commit to reading. Your streak continues as long as you hit your weekly goal!
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Days per week</label>
          <Select
            value={weeklyGoal.toString()}
            onValueChange={(value) => setWeeklyGoal(parseInt(value))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 days - Casual</SelectItem>
              <SelectItem value="3">3 days - Light</SelectItem>
              <SelectItem value="4">4 days - Regular</SelectItem>
              <SelectItem value="5">5 days - Committed</SelectItem>
              <SelectItem value="6">6 days - Dedicated</SelectItem>
              <SelectItem value="7">7 days - Daily</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-muted rounded-lg p-3 text-sm text-muted-foreground">
          <p className="mb-2"><strong>How it works:</strong></p>
          <ul className="space-y-1 ml-4">
            <li>• Log reading activity on {weeklyGoal} days each week to maintain your streak</li>
            <li>• Exceed your goal? Bonus days get added to your streak!</li>
            <li>• Miss by one day? A streak freeze will protect you</li>
          </ul>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Goal'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
